import { Box, Divider, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { AGENTS_PATH } from '../../config';
import { cardSurfaceSx, contentTypeColourFor, tokens } from '../../theme';
import type { AgentSearchResultCardProps } from './types';

/**
 * An Agent Skill among the search results, drawn like the catalog's cards where it can
 * be and differently where it cannot.
 *
 * **There is no stage edge and no reference**, because an Agent Skill has neither: it has
 * no stage of its own (search places it under its methods') and holds no place in the
 * tree, so a coloured edge or a
 * number would claim a position it does not have. The 4px edge stays, in the
 * neutral border colour, so a column mixing both kinds keeps one rhythm.
 *
 * **The type pill goes through `contentTypeColourFor`, which has no `Agent` entry
 * and answers with the neutral badge.** That fallback is the point: a type the
 * theme has not been taught looks plainly uncoloured rather than plausibly
 * coloured, and Agent Skills have not been given a hue of their own.
 *
 * The title is the link and the card is not, as the catalog's are, because the
 * methods named below it are text and one destination is enough.
 */
export const AgentSearchResultCard = ({ agent }: AgentSearchResultCardProps) => {
  const theme = useTheme();
  const badge = contentTypeColourFor(theme.palette, 'Agent');

  return (
    <Box component="li" sx={{ ...cardSurfaceSx, overflow: 'hidden', listStyle: 'none' }}>
      <Box sx={{ height: 4, backgroundColor: 'border.strong' }} />

      <Box sx={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}
        >
          <Typography variant="caption" component="p" sx={{ color: 'text.meta' }}>
            Toolkit
            <Box component="span" aria-hidden sx={{ paddingInline: 2, color: 'text.disabled' }}>
              &bull;
            </Box>
            Version {agent.version}
          </Typography>

          <Typography
            variant="caption"
            component="span"
            sx={{
              flexShrink: 0,
              paddingInline: 3,
              paddingBlock: 1,
              borderRadius: `${String(tokens.radius.full)}px`,
              backgroundColor: badge.surface,
              color: badge.colour,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            Agent Skill
          </Typography>
        </Box>

        <Typography variant="h3" component="h3">
          <RouterLink
            to={`${AGENTS_PATH}/${encodeURIComponent(agent.name)}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {agent.title}
          </RouterLink>
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {agent.summary}
        </Typography>

        {agent.methods.length > 0 && (
          <>
            <Divider />
            <Typography variant="caption" component="p" sx={{ color: 'text.meta' }}>
              Supports {agent.methods.map((method) => method.name).join(', ')}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
